'use client';

import { CatSkeleton } from '@blocks-react/components/Skeleton';
import {
  Conditional,
  Map,
  MapControl,
  MapMarker,
  getBounds,
  getLatLng
} from '../../common';
import { DealerStore } from '../dealerStoreType';
import { CatButton } from '@blocks-react/components/Button';
import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from '../DealerLocator.module.scss';
import { ACTIVE_MARKER_ICON, INACTIVE_MARKER_ICON } from './constants';
import {
  DEFAULT_MAP_BOUNDS,
  DEFAULT_MAP_ZOOM,
  MAP_MARKER_MAX_WIDTH,
  MAP_MARKER_MIN_WIDTH,
  SELECTED_STORE_ID
} from '../constants';
import { roundCompare } from '../utils';
import { CatHeading } from '@blocks-react/components/Heading';
import { useFormContext } from 'react-hook-form';

interface DealerLocatorMapContainerProps {
  userCountryCode: string;
  dealerCode: string;
  dealerStores: DealerStore[];
  selectedStore: DealerStore;
  onSearchThisAreaClick: (arg1: { lat: number; lng: number }) => void;
  searchThisAreaBtnText: string;
  loadingResults: boolean;
}

export const DealerLocatorMapContainer = ({
  userCountryCode,
  dealerCode,
  dealerStores,
  selectedStore,
  onSearchThisAreaClick,
  searchThisAreaBtnText,
  loadingResults
}: DealerLocatorMapContainerProps) => {
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM);
  const [bounds, setBounds] = useState(DEFAULT_MAP_BOUNDS);
  const [showSearchThisAreaBtn, setShowSearchThisAreaBtn] = useState(false);
  const [showMarkerDetails, setShowMarkerDetails] = useState(false);
  const initialCenter = useRef({ lat: 0, lng: 0 });
  const activeMarker = useRef('');
  const { setValue } = useFormContext();
  const dealerInfoStyles = cx(styles['dealer-locator-text'], 'mb-0');
  const markerInfoOptions = {
    minWidth: MAP_MARKER_MIN_WIDTH,
    maxWidth: MAP_MARKER_MAX_WIDTH
  };

  const isSelectedStoreInDealerStores =
    (dealerStores.includes(selectedStore) && dealerStores[0]) || !selectedStore;
  const visibleStores = isSelectedStoreInDealerStores
    ? dealerStores
    : [...dealerStores.slice(0, dealerStores.length - 1), selectedStore];

  // Set new bounds, active marker, and center for map when a new location is selected
  useEffect(() => {
    if (selectedStore === undefined) {
      activeMarker.current = '';
    }
    const isNewLocation =
      dealerStores.length &&
      !!selectedStore &&
      selectedStore.id.toString() !== activeMarker.current;
    if (isNewLocation) {
      setShowMarkerDetails(false);
      setShowSearchThisAreaBtn(false);
      const newBounds = getBounds(
        dealerStores.map((store) => {
          return getLatLng(store.geo.latitude, store.geo.longitude);
        })
      );
      setBounds(newBounds);
      activeMarker.current = selectedStore.id.toString();
      const closestStore = getLatLng(
        selectedStore.geo.latitude,
        selectedStore.geo.longitude
      );
      setCenter(closestStore);
      initialCenter.current = { lat: closestStore.lat, lng: closestStore.lng };
    }
  }, [dealerStores, selectedStore]);

  // show search this area button if the center is has been moved from the initial map center
  useEffect(() => {
    if (
      initialCenter.current.lat &&
      initialCenter.current.lng &&
      !showSearchThisAreaBtn
    ) {
      const isInitialCenter =
        roundCompare(initialCenter?.current.lat, center.lat, 7) &&
        roundCompare(initialCenter?.current.lng, center.lng, 7);
      if (!isInitialCenter && center.lat && center.lng) {
        setShowSearchThisAreaBtn(true);
      }
    }
  }, [center, showSearchThisAreaBtn]);

  const handleActiveMarker = (store?: DealerStore) => {
    if (store) {
      activeMarker.current = store.id.toString();
      setValue(SELECTED_STORE_ID, store.id.toString());
      setShowMarkerDetails(true);
      setCenter({ lat: store.geo.latitude + 0.8, lng: store.geo.longitude });
    } else {
      setShowMarkerDetails(false);
    }
  };

  const getContentElement = (isActiveStore: boolean) => {
    const storeIcon = isActiveStore ? ACTIVE_MARKER_ICON : INACTIVE_MARKER_ICON;
    const storeIconElement = document.createElement('img');
    storeIconElement.src = storeIcon;
    return storeIconElement;
  };

  return (
    <div
      className="my-3"
      style={{ height: '400px' }}
      data-testid="map-container"
    >
      <Conditional
        test={!loadingResults && !!selectedStore}
        fallback={
          <div className={styles['map-container']}>
            <CatSkeleton size="xxl" />
          </div>
        }
      >
        <Map
          userCountryCode={userCountryCode}
          dealerCode={dealerCode}
          zoom={zoom}
          onZoomChange={setZoom}
          center={center}
          onCenterChange={() => setCenter}
          bounds={bounds}
          className={styles['map-container']}
          onClick={() => handleActiveMarker()}
          mapOptions={{
            disableDefaultUI: true,
            clickableIcons: false,
            fullscreenControl: false,
            zoomControl: true
          }}
        >
          <MapControl>
            <CatButton
              variant="contrast"
              className={cx('m-2', { 'd-none': !showSearchThisAreaBtn })}
              onClick={() => onSearchThisAreaClick(center)}
            >
              {searchThisAreaBtnText}
            </CatButton>
          </MapControl>
          {visibleStores?.map((store) => {
            const {
              id,
              geo: { latitude, longitude },
              dealerName,
              address: { city, state, phone, line1 }
            } = store;
            const isActiveStore = id.toString() === activeMarker.current;
            const storeIconElement = getContentElement(isActiveStore);
            const storeZIndex = isActiveStore ? 1 : 0;
            return (
              <MapMarker
                key={id}
                data-testid={`map-marker-${id}`}
                position={getLatLng(latitude, longitude)}
                showInfo={isActiveStore && showMarkerDetails}
                onClick={() => handleActiveMarker(store)}
                content={storeIconElement}
                zIndex={storeZIndex}
                markerInfoOptions={markerInfoOptions}
              >
                <div
                  className={`cat-u-theme-typography-body-sm ${styles['map-details-container']}`}
                >
                  <CatHeading
                    variant="label-lg"
                    className="cat-u-theme-typography-label-lg"
                  >
                    {dealerName}
                  </CatHeading>
                  {city && <p className={dealerInfoStyles}>{city}</p>}
                  {line1 && <p className={dealerInfoStyles}>{line1}</p>}
                  {city && state && (
                    <p className={dealerInfoStyles}>{`${city}, ${state}`}</p>
                  )}
                  {phone && <p className={dealerInfoStyles}>{phone}</p>}
                </div>
              </MapMarker>
            );
          })}
        </Map>
      </Conditional>
    </div>
  );
};
